import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setNotification } from "features/Notification/notificationSlice";
import authHeader from "services/auth-header";
import API from "services/axios";

const EMPTY_REQUEST = {
  trackingNumber: "",
  customerName: "",
  contactName: "",
  address1: "",
  address2: "",
  city: "",
  state: "",
  zip: "",
  country: "",
  service: "",
  packages: "",
  weight: "",
  shippingCost: "",
  void: "",
  startDate: "",
  endDate: "",
};

export default function UpsAdvanceSearchModal({ showModal, setSearchResult }) {
  const dispatch = useDispatch();

  const [requestList, setRequestList] = useState([
    { requestId: 1, ...EMPTY_REQUEST },
  ]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(requestList[0]);

  useEffect(() => {
    if (requestList.length > 0 && currentIndex < requestList.length) {
      setFormData(requestList[currentIndex]);
    }
  }, [currentIndex, requestList]);

  const nextRequest = () => {
    if (currentIndex < requestList.length - 1) setCurrentIndex(currentIndex + 1);
  };

  const previousRequest = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const createNewRequest = () => {
    const maxId = requestList.reduce((max, item) => Math.max(max, item.requestId), 0);
    const updatedList = [...requestList, { requestId: maxId + 1, ...EMPTY_REQUEST }];
    setRequestList(updatedList);
    setCurrentIndex(updatedList.length - 1);
  };

  const duplicateRequest = () => {
    const maxId = requestList.reduce((max, item) => Math.max(max, item.requestId), 0);
    const updatedList = [
      ...requestList,
      { ...requestList[currentIndex], requestId: maxId + 1 },
    ];
    setRequestList(updatedList);
    setCurrentIndex(updatedList.length - 1);
    dispatch(setNotification({ message: "Request duplicated", type: "success" }));
  };

  const clearCurrentRequest = () => {
    const cleared = { requestId: requestList[currentIndex].requestId, ...EMPTY_REQUEST };
    const updatedList = [...requestList];
    updatedList[currentIndex] = cleared;
    setRequestList(updatedList);
    setFormData(cleared);
    dispatch(setNotification({ message: "Request cleared", type: "success" }));
  };

  const deleteRequest = () => {
    if (requestList.length <= 1) {
      dispatch(
        setNotification({ message: "Cannot delete the last request", type: "warning" }),
      );
      return;
    }
    const updatedList = requestList.filter((_, i) => i !== currentIndex);
    setRequestList(updatedList);
    if (currentIndex >= updatedList.length) setCurrentIndex(updatedList.length - 1);
    dispatch(setNotification({ message: "Request deleted", type: "success" }));
  };

  const updateField = (field, value) => {
    const updatedList = [...requestList];
    updatedList[currentIndex] = { ...updatedList[currentIndex], [field]: value };
    setRequestList(updatedList);
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      const payload = requestList.map((req) => ({
        ...req,
        startDate: req.startDate ? `${req.startDate} 00:00:00` : "",
        endDate: req.endDate ? `${req.endDate} 23:59:59` : "",
      }));
      const { data } = await API.post("/ups-trackings/advance-search", payload, {
        headers: authHeader(),
      });
      if (data?.success) {
        setSearchResult(data.data || []);
        showModal(false);
      } else {
        dispatch(
          setNotification({ message: data?.message || "Search failed", type: "error" }),
        );
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  const fields = [
    { key: "trackingNumber", label: "Tracking #" },
    { key: "customerName", label: "Customer" },
    { key: "contactName", label: "Contact" },
    { key: "address1", label: "Address 1" },
    { key: "address2", label: "Address 2" },
    { key: "city", label: "City" },
    { key: "state", label: "State" },
    { key: "zip", label: "Zip" },
    { key: "country", label: "Country" },
    { key: "service", label: "Service" },
    { key: "packages", label: "Packages" },
    { key: "weight", label: "Weight" },
    { key: "shippingCost", label: "Shipping Cost" },
  ];

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center gap-1">
        <h3 className="mt-0">Build A Find</h3>
        <div className="d-flex align-items-center gap-1">
          <div className="me-3">
            Showing <span className="text-primary">{currentIndex + 1}</span> of{" "}
            <span className="text-primary">{requestList.length}</span>
          </div>
          <button
            onClick={createNewRequest}
            disabled={loading}
            className="btn btn-success btn-sm p-0 px-1"
            title="Create New Request"
          >
            <i className="mdi mdi-plus fs-3 py-0 my-0"></i>
          </button>
          <button
            onClick={duplicateRequest}
            disabled={loading}
            className="btn btn-warning btn-sm p-0 px-1"
            title="Duplicate Request"
          >
            <i className="mdi mdi-content-duplicate fs-3 py-0 my-0"></i>
          </button>
          <button
            onClick={deleteRequest}
            disabled={requestList.length <= 1 || loading}
            className="btn btn-danger btn-sm p-0 px-1"
            title="Delete Request"
          >
            <i className="mdi mdi-delete fs-3 py-0 my-0"></i>
          </button>
          <div className="border-start border-3 mx-2" style={{ height: 35, width: 1 }}></div>
          <button
            onClick={previousRequest}
            disabled={currentIndex <= 0 || loading}
            className="btn btn-success btn-sm p-0 px-1"
            title="Previous Request"
          >
            <i className="mdi mdi-arrow-left-bold fs-3 py-0 my-0"></i>
          </button>
          <button
            onClick={nextRequest}
            disabled={currentIndex >= requestList.length - 1 || loading}
            className="btn btn-success btn-sm p-0 px-1"
            title="Next Request"
          >
            <i className="mdi mdi-arrow-right-bold fs-3 py-0 my-0"></i>
          </button>
        </div>
      </div>
      <hr className="mt-1" />

      <div className="row">
        {fields.map((f) => (
          <div className="col-6 mb-2" key={f.key}>
            <label className="form-label mb-0">{f.label}</label>
            <input
              type="text"
              className="form-control"
              placeholder={`🔎 ${f.label}`}
              value={formData?.[f.key] || ""}
              onChange={(e) => updateField(f.key, e.target.value)}
            />
          </div>
        ))}
        <div className="col-6 mb-2">
          <label className="form-label mb-0">Void</label>
          <select
            className="form-control"
            value={formData?.void || ""}
            onChange={(e) => updateField("void", e.target.value)}
          >
            <option value="">Any</option>
            <option value="Y">Voided</option>
            <option value="N">Not Voided</option>
          </select>
        </div>
        <div className="col-3 mb-2">
          <label className="form-label mb-0">Ship Date From</label>
          <input
            type="date"
            className="form-control"
            value={formData?.startDate || ""}
            onChange={(e) => updateField("startDate", e.target.value)}
          />
        </div>
        <div className="col-3 mb-2">
          <label className="form-label mb-0">Ship Date To</label>
          <input
            type="date"
            className="form-control"
            value={formData?.endDate || ""}
            onChange={(e) => updateField("endDate", e.target.value)}
          />
        </div>
      </div>

      <div className="text-end mt-2">
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => showModal(false)}
        >
          Close
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm ms-1"
          disabled={loading}
          onClick={handleSearch}
        >
          Search
        </button>
        <button
          type="button"
          className="btn btn-outline-danger btn-sm ms-1"
          disabled={loading}
          onClick={clearCurrentRequest}
          title="Clear Current Request"
        >
          Clear
        </button>
      </div>
    </div>
  );
}
